/**
 * The single place that talks to the server.
 *
 * Access tokens live in memory only — never localStorage — so an XSS bug
 * cannot walk off with one. The long-lived refresh token is an HttpOnly
 * cookie the browser handles and JavaScript cannot read. A 401 triggers one
 * silent refresh and one retry; a second failure signs the user out.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  get isAuth() {
    return this.status === 401
  }
  get isForbidden() {
    return this.status === 403
  }
  get isNotFound() {
    return this.status === 404
  }
  get isConflict() {
    return this.status === 409
  }
}

let accessToken: string | null = null
let onSignedOut: (() => void) | null = null

/** Concurrent 401s must share one refresh, or a page with six queries fires six. */
let refreshInFlight: Promise<boolean> | null = null

export function setAccessToken(token: string | null) {
  accessToken = token
}

export function getAccessToken() {
  return accessToken
}

export function onUnauthenticated(fn: () => void) {
  onSignedOut = fn
}

const BASE = '/api/v1'

type Options = {
  method?: string
  body?: unknown
  signal?: AbortSignal
  /** Set for the refresh call itself, which must not recurse. */
  noRetry?: boolean
  raw?: boolean
  /** The path is complete as given (e.g. /video/v1/…) rather than under BASE. */
  absolute?: boolean
}

export async function api<T = unknown>(path: string, opts: Options = {}): Promise<T> {
  const res = await send(path, opts)

  if (res.status === 401 && !opts.noRetry) {
    const refreshed = await refreshOnce()
    if (refreshed) {
      const retry = await send(path, opts)
      return unwrap<T>(retry, opts)
    }
    accessToken = null
    onSignedOut?.()
    throw new ApiError(401, 'Your session has expired. Please sign in again.')
  }

  return unwrap<T>(res, opts)
}

async function send(path: string, opts: Options) {
  const headers: Record<string, string> = { Accept: 'application/json' }
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json'

  return fetch((opts.absolute ? '' : BASE) + path, {
    method: opts.method ?? (opts.body !== undefined ? 'POST' : 'GET'),
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    credentials: 'include',
    signal: opts.signal,
  })
}

async function unwrap<T>(res: Response, opts: Options): Promise<T> {
  if (opts.raw) return res as unknown as T

  if (!res.ok) {
    let message = res.statusText || `Request failed (${res.status})`
    try {
      const body = await res.json()
      if (body?.error) message = body.error
    } catch {
      // A non-JSON error body (a proxy's 502 page, say) is not worth parsing.
    }
    throw new ApiError(res.status, message)
  }

  if (res.status === 204) return undefined as T
  const text = await res.text()
  return text ? (JSON.parse(text) as T) : (undefined as T)
}

async function refreshOnce(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      const res = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      })
      if (!res.ok) return false
      const data = (await res.json()) as { accessToken: string }
      accessToken = data.accessToken
      return true
    } catch {
      return false
    } finally {
      // Cleared on the next tick so simultaneous callers all observe the
      // result of this attempt rather than starting another.
      queueMicrotask(() => {
        refreshInFlight = null
      })
    }
  })()

  return refreshInFlight
}

/** Builds a query string, dropping empty values so URLs stay readable. */
export function qs(params: Record<string, string | number | boolean | undefined | null | string[]>) {
  const sp = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue
    if (Array.isArray(v)) {
      if (v.length) sp.set(k, v.join(','))
      continue
    }
    sp.set(k, String(v))
  }
  const s = sp.toString()
  return s ? `?${s}` : ''
}

/** Downloads a CSV export without leaving the page. */
export async function download(path: string, filename: string) {
  const res = await send(path, { raw: true })
  if (res.status === 401) {
    if (await refreshOnce()) return download(path, filename)
  }
  if (!res.ok) throw new ApiError(res.status, 'Export failed')

  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}
